import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, MessageSquare, Phone, ChevronDown, Search } from "lucide-react";
import { faqGroups } from "@/components/sections/Faq";

export default function HelpCenter() {
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState(faqGroups[0].category);
  const [openIdx, setOpenIdx] = useState(0);

  const filtered = faqGroups
    .find((g) => g.category === activeCat)
    .items.filter(
      (it) =>
        !query ||
        it.q.toLowerCase().includes(query.toLowerCase()) ||
        it.a.toLowerCase().includes(query.toLowerCase()),
    );

  return (
    <main className="relative pt-40 pb-24">
      <div className="aurora-orb bg-[#0047FF] w-[600px] h-[600px] -top-20 left-1/2 -translate-x-1/2 opacity-30" />
      <div className="container-x relative">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-xs uppercase tracking-[0.25em] text-[#00F0FF] mb-4">
            Support
          </div>
          <h1 className="font-display text-5xl md:text-6xl font-bold mb-4">
            Help <span className="aurora-text">Center</span>
          </h1>
          <p className="text-white/65">
            Find answers to common questions or get in touch with our support
            team.
          </p>

          <div className="relative mt-8 max-w-xl mx-auto">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="help-search"
              placeholder="Search help articles…"
              className="w-full pl-12 pr-5 py-4 rounded-full bg-white/5 border border-white/10 focus:border-[#00F0FF]/50 focus:outline-none text-white placeholder:text-white/30"
            />
          </div>
        </div>

        <h2 className="font-display text-3xl font-bold mb-6 text-center">
          Frequently Asked <span className="cyan-text">Questions</span>
        </h2>

        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {faqGroups.map((g) => (
            <button
              key={g.category}
              onClick={() => {
                setActiveCat(g.category);
                setOpenIdx(0);
              }}
              className={`px-4 py-2 rounded-full text-sm transition-all ${
                activeCat === g.category
                  ? "bg-gradient-to-r from-[#0047FF] to-[#00F0FF] text-white"
                  : "border border-white/10 text-white/60 hover:text-white hover:border-white/30"
              }`}
            >
              {g.category}
            </button>
          ))}
        </div>

        <div className="max-w-3xl mx-auto mb-20">
          {filtered.length === 0 && (
            <div className="text-center text-white/50 py-10">
              No articles match "{query}".
            </div>
          )}
          {filtered.map((it, i) => (
            <div key={it.q} className="border-b border-white/10">
              <button
                onClick={() => setOpenIdx(openIdx === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-6 py-6 text-left"
              >
                <span className="font-display text-lg md:text-xl font-medium pr-4">
                  {it.q}
                </span>
                <ChevronDown
                  className={`w-5 h-5 text-[#00F0FF] shrink-0 transition-transform ${
                    openIdx === i ? "rotate-180" : ""
                  }`}
                />
              </button>
              <AnimatePresence initial={false}>
                {openIdx === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <p className="text-white/65 pb-6 pr-10 leading-relaxed">
                      {it.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>

        <div className="text-center max-w-2xl mx-auto mb-10">
          <h2 className="font-display text-3xl md:text-4xl font-bold">
            Still Need <span className="cyan-text">Help?</span>
          </h2>
          <p className="text-white/65 mt-3">
            Our support team is here to help you succeed. Get in touch with us
            directly.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <ContactCard
            icon={Mail}
            title="Email Support"
            desc="Get help via email"
            action={{
              label: "contact@monetiseup.com",
              href: "mailto:contact@monetiseup.com",
            }}
          />
          <ContactCard
            icon={MessageSquare}
            title="Live Chat"
            desc="Chat with our team"
            action={{ label: "Start Chat", href: "#" }}
            primary
          />
          <ContactCard
            icon={Phone}
            title="Schedule Call"
            desc="Talk to an expert"
            action={{
              label: "Book Call",
              href: "mailto:contact@monetiseup.com?subject=Schedule%20Call",
            }}
          />
        </div>

        <div className="text-center mt-12">
          <a
            href="mailto:contact@monetiseup.com"
            className="btn-primary"
          >
            Contact Our Team
          </a>
        </div>
      </div>
    </main>
  );
}

function ContactCard({ icon: Icon, title, desc, action, primary }) {
  return (
    <div className="tile p-7 text-center">
      <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-[#0047FF]/30 to-[#00F0FF]/20 border border-white/10 grid place-items-center mb-4">
        <Icon className="w-5 h-5 text-[#00F0FF]" />
      </div>
      <h3 className="font-display text-xl font-bold mb-1">{title}</h3>
      <p className="text-sm text-white/55 mb-5">{desc}</p>
      <a
        href={action.href}
        className={primary ? "btn-primary" : "btn-ghost"}
      >
        {action.label}
      </a>
    </div>
  );
}
